import {
  getLocale, isLocaleCode, matchLocaleTag, publishedLocales, resolveInterfaceLocale,
  type LanguagePreference, type LocaleCode,
} from "./localeRegistry";
import { interfaceLanguageHintParam } from "./interfaceLanguageHandoff";
import { nativeInterfaceLanguages } from "./nativeInterfaceLanguages";
export { interfaceLanguageHintParam } from "./interfaceLanguageHandoff";

export const interfaceLanguageGuestKey = "jungle.interface-language.guest.v1";
export const interfaceLanguageAccountKey = "jungle.interface-language.account.v1";
export const availableInterfaceLocales = publishedLocales("app");
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface InterfaceLanguageAuthority {
  preference: LanguagePreference;
  effectiveLocale: LocaleCode;
  revision: string | null;
  updatedAt: string | null;
}
export interface CachedInterfaceLanguage extends InterfaceLanguageAuthority { accountId: string }
export interface InitialInterfaceLanguage {
  preference: LanguagePreference;
  effectiveLocale: LocaleCode;
  source: "account" | "guest" | "handoff" | "system";
}

export function isSelectableInterfacePreference(value: unknown): value is LanguagePreference {
  return value === "system" || (isLocaleCode(value) && availableInterfaceLocales.includes(value));
}

/** Provider response parsing is separate from selecting a new published edition. */
export function parseInterfaceLanguageAuthority(value: unknown): InterfaceLanguageAuthority | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  // A saved explicit edition may be unavailable, but its effective catalog must be published.
  const preferenceIsValid = row.preference === "system" || isLocaleCode(row.preference);
  const effectiveLocale = isLocaleCode(row.effectiveLocale) && availableInterfaceLocales.includes(row.effectiveLocale) ? row.effectiveLocale : null;
  const revisionIsValid = row.revision === null || (typeof row.revision === "string" && /^[A-Za-z0-9-]{1,80}$/.test(row.revision));
  const updatedAtIsValid = row.updatedAt === null || (typeof row.updatedAt === "string" && Number.isFinite(Date.parse(row.updatedAt)));
  if (!preferenceIsValid || !effectiveLocale || !revisionIsValid || !updatedAtIsValid) return null;
  return { preference: row.preference as LanguagePreference, effectiveLocale,
    revision: row.revision as string | null, updatedAt: row.updatedAt as string | null };
}

function readJson(storage: StorageLike | undefined, key: string): unknown {
  try {
    const value = storage?.getItem(key);
    return value && value.length <= 2048 ? JSON.parse(value) : null;
  } catch { return null; }
}

export function readGuestInterfacePreference(storage?: StorageLike): LanguagePreference | null {
  const value = readJson(storage, interfaceLanguageGuestKey);
  return isSelectableInterfacePreference(value) ? value : null;
}

export function readCachedInterfaceLanguage(storage?: StorageLike): CachedInterfaceLanguage | null {
  const value = readJson(storage, interfaceLanguageAccountKey) as Record<string, unknown> | null;
  const authority = parseInterfaceLanguageAuthority(value);
  return authority && typeof value?.accountId === "string" && value.accountId.length > 0 && value.accountId.length <= 128
    ? { ...authority, accountId: value.accountId } : null;
}

export function cacheInterfaceLanguage(storage: StorageLike | undefined, accountId: string, authority: InterfaceLanguageAuthority) {
  try { storage?.setItem(interfaceLanguageAccountKey, JSON.stringify({ ...authority, accountId })); } catch { /* Memory still works. */ }
}
export function rememberGuestInterfacePreference(storage: StorageLike | undefined, preference: LanguagePreference) {
  if (!isSelectableInterfacePreference(preference)) return;
  try { storage?.setItem(interfaceLanguageGuestKey, JSON.stringify(preference)); } catch { /* Memory still works. */ }
}
export function clearCachedInterfaceLanguage(storage?: StorageLike) {
  try { storage?.removeItem(interfaceLanguageAccountKey); } catch { /* Storage may be blocked. */ }
}
export function getInterfaceLanguageStorage(): StorageLike | undefined {
  try { return typeof window === "undefined" ? undefined : window.localStorage; } catch { return undefined; }
}
export function browserInterfaceLanguages(): string[] {
  const native = nativeInterfaceLanguages();
  if (native) return [...native];
  if (typeof navigator === "undefined") return ["en"];
  return navigator.languages?.length ? [...navigator.languages] : [navigator.language || "en"];
}

/** A marketing/native hint is an arrival hint, never an account preference write. */
export function readInterfaceLanguageHint(search: string): LocaleCode | null {
  const values = new URLSearchParams(search).getAll(interfaceLanguageHintParam);
  return values.length === 1 && isLocaleCode(values[0]) && availableInterfaceLocales.includes(values[0]) ? values[0] : null;
}

export function initialInterfaceLanguage({ storage = getInterfaceLanguageStorage(), languages = browserInterfaceLanguages(),
  search = typeof window === "undefined" ? "" : window.location.search,
  includeAccount = true,
}: { storage?: StorageLike; languages?: readonly string[]; search?: string; includeAccount?: boolean } = {}): InitialInterfaceLanguage {
  const account = includeAccount ? readCachedInterfaceLanguage(storage) : null;
  const guest = readGuestInterfacePreference(storage);
  const hint = readInterfaceLanguageHint(search);
  const preference = account?.preference ?? guest ?? hint ?? "system";
  return { preference, effectiveLocale: resolveInterfaceLocale(preference, [...languages]),
    source: account ? "account" : guest ? "guest" : hint ? "handoff" : "system" };
}

/** Legacy absence means system; unavailable explicit scripts still resolve to English. */
export function interfaceAuthorityFromSettings(settings?: Record<string, unknown>): InterfaceLanguageAuthority {
  const preference = settings?.app_language_preference === "system" ? "system" : matchLocaleTag(settings?.app_language_preference) ?? "system";
  const revision = typeof settings?.app_language_preference_revision === "string" && /^[A-Za-z0-9-]{1,80}$/.test(settings.app_language_preference_revision)
    ? settings.app_language_preference_revision : null;
  return { preference, revision,
    effectiveLocale: resolveInterfaceLocale(preference, typeof settings?.app_language === "string" ? [settings.app_language] : ["en"]),
    updatedAt: typeof settings?.app_language_updated_at === "string" && Number.isFinite(Date.parse(settings.app_language_updated_at)) ? settings.app_language_updated_at : null };
}

export const interfaceLocaleLabel = (locale: LocaleCode) => getLocale(locale).nativeName;
